import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import { useErrorMessage } from "@/shared/errors";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { usePrivateToast } from "@/shared/usePrivateToast";

import type { ServiceRequest } from "./serviceRequests";

/**
 * The store's OPEN diner asks (D132), for whoever works the floor
 * (`RESTAURANT_POS_USE`, the route's own gate). One query key for the chime,
 * the floor's badges and the bill's list, so a screen showing all three still
 * shares one revision-driven request.
 */
export function useServiceRequests() {
  const { data: session } = useSession();
  const enabled = hasPermission(session, PERMISSIONS.POS_USE);
  return useApiQuery<ServiceRequest[]>(
    queryKeys.serviceRequests,
    endpoints.restaurant.serviceRequests,
    {
      enabled,
      retry: false,
    },
  );
}

/**
 * Marks an ask done. Not an invoice command — an ask moves nothing on a bill —
 * so it is a plain POST. The row leaves the cached list at once, then the list
 * is re-read so a second person's Done on another device lands too.
 */
export function useMarkServiceRequestDone() {
  const queryClient = useQueryClient();
  const toast = usePrivateToast();
  const errorMessage = useErrorMessage();
  const [busyId, setBusyId] = useState<string | null>(null);

  const markDone = async (id: string) => {
    setBusyId(id);
    try {
      await apiFetch(endpoints.restaurant.serviceRequestDone(id), { method: "POST" });
      queryClient.setQueryData<ServiceRequest[]>(queryKeys.serviceRequests, (current) =>
        current?.filter((request) => request.id !== id),
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.serviceRequests });
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setBusyId(null);
    }
  };

  return { markDone, busyId };
}
